import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight, faMagnifyingGlass, faSync } from "@fortawesome/free-solid-svg-icons";
import {Fragment, useEffect, useState} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import classNames from "classnames/bind";
import Table from '../Table';
import SkeletonTableLoading from '../SkeletonTableLoading';
import api from "~/api/api";
import CommonAPI from "~/api/commonApi";
import Select from 'react-select';
import styles from "./TablePaging.module.scss";

const cx = classNames.bind(styles);
function TablePaging({tablePagingConfig, reloadTable, ableSearch = true, onDataLoaded}) {
    const {tableKey = "Empty", searchPlaceholder = "Search...", searchDescription = ""} = tablePagingConfig;
    
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const initialSearchId = queryParams.get('id') || queryParams.get('searchId') || null;

    const [tableState, setTableState] = useState({
        data: [],
        totalRecord: 0,
        totalPage: 1,
    });
    const [pageSize, setPageSize] = useState(10);
    const [pageNumber, setPageNumber] = useState(1);
    const [searchString, setSearchString] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const [searchId, setSearchId] = useState(initialSearchId);
    const [loading, setLoading] = useState(true);
    const [settingsLoaded, setSettingsLoaded] = useState(false);
    const [localReloadKey, setLocalReloadKey] = useState(0);

    const [sortColumn, setSortColumn] = useState("");
    const [sortDirection, setSortDirection] = useState("ASC");

    useEffect(() => {
        CommonAPI.getColumnSetting(tableKey)
            .then(res => {
                if(res.data?.RowPerPage){
                    setPageSize(parseInt(res.data.RowPerPage, 10));
                }
                if(res.data?.SortColumn){
                    setSortColumn(res.data.SortColumn);
                    setSortDirection(res.data.SortDirection || "ASC");
                }
            })
            .catch(err => {})
            .finally(() => {
                setSettingsLoaded(true);
            });
    }, []);

    useEffect(() => {
        if (!settingsLoaded) return;
        setLoading(true);

        if (tablePagingConfig.isClientSide) {
            if (tablePagingConfig.externalLoading) {
                setLoading(true);
                return;
            }

            let processedData = [...(tablePagingConfig.clientData || [])];
            
            // 1. Search
            if (searchString) {
                const lowerSearch = searchString.toLowerCase();
                const searchKeys = tablePagingConfig.searchKeys || [];
                if (searchKeys.length > 0) {
                    processedData = processedData.filter(item => 
                        searchKeys.some(key => 
                            item[key] != null && String(item[key]).toLowerCase().includes(lowerSearch)
                        )
                    );
                } else {
                    processedData = processedData.filter(item => 
                        Object.values(item).some(val => 
                            val != null && String(val).toLowerCase().includes(lowerSearch)
                        )
                    );
                }
            }

            // 2. Sort
            if (sortColumn) {
                processedData.sort((a, b) => {
                    let valA = a[sortColumn];
                    let valB = b[sortColumn];
                    if (valA == null) valA = '';
                    if (valB == null) valB = '';
                    if (typeof valA === 'string') valA = valA.toLowerCase();
                    if (typeof valB === 'string') valB = valB.toLowerCase();

                    if (valA < valB) return sortDirection === "ASC" ? -1 : 1;
                    if (valA > valB) return sortDirection === "ASC" ? 1 : -1;
                    return 0;
                });
            }

            // 3. Pagination
            const totalRecord = processedData.length;
            const totalPage = totalRecord === 0 ? 1 : Math.ceil(totalRecord / pageSize);
            
            // Ensure pageNumber is within bounds
            const validPageNumber = Math.min(Math.max(1, pageNumber), totalPage);
            if (validPageNumber !== pageNumber) {
                setPageNumber(validPageNumber);
                // Return early so the effect will run again with the corrected pageNumber
                return;
            }

            const startIndex = (pageNumber - 1) * pageSize;
            const pagedData = processedData.slice(startIndex, startIndex + pageSize);

            setTableState({
                data: pagedData,
                totalRecord,
                totalPage,
            });
            if (onDataLoaded) onDataLoaded(pagedData);
            setLoading(false);
            return;
        }

        const params = new URLSearchParams({
            PageSize: pageSize,
            PageNumber: pageNumber,
            searchString,
            ...(tablePagingConfig.extraParams || {})
        });

        if (searchId) {
            params.append("searchId", searchId);
        }

        if (sortColumn) {
            params.append("OrderBy", `${sortColumn} ${sortDirection}`);
        }

        api.get(`${tablePagingConfig.url}?${params}`)
            .then((res) => {
                const totalRecord = res.data.TotalRecord !== undefined ? res.data.TotalRecord : res.data.totalRecord;
                const results = res.data.Results !== undefined ? res.data.Results : res.data.results;
                const totalPage = totalRecord === 0 ? 1 : Math.ceil(totalRecord / pageSize);

                setTableState({
                    data: results,
                    totalRecord,
                    totalPage,
                });
                if (onDataLoaded) onDataLoaded(results);
            })
            .catch((err) => {
            })
            .finally(() => {
                setLoading(false);
            })
    }, [settingsLoaded, pageSize, pageNumber, searchString, searchId, sortColumn, sortDirection, reloadTable, tablePagingConfig.clientData, localReloadKey, tablePagingConfig.externalLoading]);

    const handleSort = (columnKey) => {
        setLoading(true);
        let newSortDir = "ASC";
        if (sortColumn === columnKey) {
            newSortDir = sortDirection === "ASC" ? "DESC" : "ASC";
        }
        setSortColumn(columnKey);
        setSortDirection(newSortDir);
        setPageNumber(1);

        CommonAPI.insUpdColumnSetting({
            TableKey: tableKey,
            Action: "SortSetting",
            SortColumn: columnKey,
            SortDirection: newSortDir
        }).catch(() => {});
    };

    const handlePrev = () => {
        if(pageNumber <= 1) return;
        setLoading(true);
        setPageNumber(prev => prev - 1);
    };

    const handleNext = () => {
        if(pageNumber >= tableState.totalPage) return;
        setLoading(true);
        setPageNumber(prev => prev + 1);
    };

    const handlePageClick = (page) => {
        if(page === pageNumber) return;
        setLoading(true);
        setPageNumber(page);
    };

    const handleVisiblePages = () => {
        const { totalPage } = tableState;
        const pages = [];

        if (totalPage <= 7) {
            return Array.from({ length: totalPage }, (_, i) => i + 1);
        }

        pages.push(1);

        if (pageNumber > 4) {
            pages.push("...");
        }

        const start = Math.max(2, pageNumber - 1);
        const end = Math.min(totalPage - 1, pageNumber + 1);

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        if (pageNumber < totalPage - 3) {
            pages.push("...");
        }

        pages.push(totalPage);

        return pages;
    };


    const handleSearch = () => {
        setLoading(true);
        const newSearch = searchInput.trim();
        if (pageNumber === 1 && searchString === newSearch) {
            setLocalReloadKey(prev => prev + 1);
        } else {
            setPageNumber(1);
            setSearchString(newSearch);
        }
    };

    const handleClear = () => {
        setLoading(true);
        if (pageNumber === 1 && searchInput === "" && searchString === "" && !searchId) {
            setLocalReloadKey(prev => prev + 1);
        } else {
            setPageNumber(1);
            setSearchInput("");
            setSearchString("");
            
            if (searchId) {
                setSearchId(null);
                const newParams = new URLSearchParams(location.search);
                newParams.delete('id');
                newParams.delete('searchId');
                navigate({ search: newParams.toString() }, { replace: true });
            }
        }
    };

    const handleClearSearchId = () => {
        setLoading(true);
        setPageNumber(1);
        setSearchId(null);
        
        const newParams = new URLSearchParams(location.search);
        newParams.delete('id');
        newParams.delete('searchId');
        navigate({ search: newParams.toString() }, { replace: true });
    };

    const onchangePageSize = (value) => {
        setLoading(true);
        setPageSize(value);
        CommonAPI.insUpdColumnSetting(
            {
                TableKey: tableKey,
                VisibleColumns: '',
                Action: "RowPerPage",
                RowPerPage: value,
            }
        ).catch(() => {});
    }

    const renderSearchBox = (isLeftAligned = false) => (
        <div key="search-box-component" className={`d-flex flex-column align-items-${isLeftAligned ? 'start' : 'end'} mt-3 mt-md-0`}>
            <div className={cx("searchWrap", "d-flex align-items-center")}>
                <div className="position-relative shadow-sm rounded-2 overflow-hidden w-100" style={{ minWidth: '400px' }}>
                    <FontAwesomeIcon 
                        icon={faMagnifyingGlass} 
                        className="position-absolute text-body-tertiary" 
                        style={{ left: '14px', top: '50%', transform: 'translateY(-50%)' }} 
                    />
                    <input 
                        name='search' 
                        className={cx("searchInput", "form-control search-input search shadow-none border")} 
                        type="search" 
                        placeholder={searchPlaceholder}
                        aria-label="Search" 
                        value={searchInput} 
                        onChange={(e) => {
                            setSearchInput(e.target.value);
                            if (e.target.value === "") {
                                setSearchString("");
                                setPageNumber(1);
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                handleSearch();
                            }
                        }}
                        style={{ paddingLeft: '38px', paddingRight: '1rem' }}
                    />
                </div>
                {!tablePagingConfig.isMergedStyle && (
                    <button type="button" className="btn btn-light fw-semibold px-3 border text-nowrap ms-3 shadow-sm d-flex align-items-center" onClick={() => {
                        setLocalReloadKey(prev => prev + 1);
                        if (tablePagingConfig.onRefresh) tablePagingConfig.onRefresh();
                    }} style={{ borderRadius: '8px' }}>
                        <FontAwesomeIcon icon={faSync} className="me-2" spin={loading} />Refresh
                    </button>
                )}
            </div>
            {searchId && (
                <div className={`d-flex align-items-center mt-2 justify-content-${isLeftAligned ? 'start' : 'end'} w-100`}>
                    <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-3 py-2 rounded-pill d-flex align-items-center">
                        <i className="fa-solid fa-filter me-2"></i>
                        Filtering exactly by ID: <strong>{searchId}</strong>
                        <button type="button" className="btn-close btn-close-sm ms-2" aria-label="Close" onClick={handleClearSearchId} style={{ fontSize: '0.65em' }}></button>
                    </span>
                </div>
            )}
            {searchDescription && (
                <small className={`text-muted mt-2 fs-9 fw-bold text-${isLeftAligned ? 'start' : 'end'}`}><i className="fa-solid fa-circle-info me-1"></i>{searchDescription}</small>
            )}
        </div>
    );

    return(
        <div data-list={""} className={cx("pagingShell")}>
            {tablePagingConfig.customTopControls && tablePagingConfig.customTopControls({
                pageSize,
                onchangePageSize,
                setPageNumber,
                searchInput,
                setSearchInput,
                handleSearch,
                handleClear,
                searchId,
                handleClearSearchId,
                setLocalReloadKey,
                settingsLoaded,
                searchPlaceholder,
                searchDescription
            })}
            
            {!tablePagingConfig.customTopControls && !tablePagingConfig.isMergedStyle && (
                <div className={cx("topControls", "d-flex flex-wrap align-items-center py-3 px-4", "rounded-3", tablePagingConfig.topLeftButtons && tablePagingConfig.topLeftButtons.length > 0 ? "justify-content-between" : "justify-content-end", "mb-3")} style={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e2e8f0'
                }}>
                    {tablePagingConfig.topLeftButtons && tablePagingConfig.topLeftButtons.length > 0 && (
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            {tablePagingConfig.topLeftButtons.map((btn, idx) => (
                                <Fragment key={idx}>{btn}</Fragment>
                            ))}
                        </div>
                    )}
                    {ableSearch && renderSearchBox(false)}
                </div>
            )}

            <Table tableConfig={{ 
                ...tablePagingConfig, 
                data: tableState.data,
                loading: loading,
                loadingRows: pageSize,
                sortColumn,
                sortDirection,
                onSort: handleSort,
                pageNumber,
                pageSize,
                    topLeftButtons: tablePagingConfig.isMergedStyle && tablePagingConfig.topLeftButtons ? tablePagingConfig.topLeftButtons : [],
                    leftInlineButtons: [
                        ...(tablePagingConfig.leftInlineButtons || []),
                        ...(tablePagingConfig.isMergedStyle ? [] : [
                            <div key="rows-per-page" className="d-flex align-items-center mb-0">
                                <label className={cx("rowsLabel", "form-label me-2 mb-0 fw-semibold text-body-emphasis fs-9")} htmlFor="validationCustom04">Rows per page:</label>
                                {settingsLoaded ? (
                                    <Select
                                        value={{ value: pageSize, label: pageSize.toString() }}
                                        onChange={(selectedOption) => {
                                            const val = selectedOption.value;
                                            onchangePageSize(val);
                                            setPageNumber(1);
                                        }}
                                        options={[10, 20, 50, 100, 200].map(n => ({ value: n, label: n.toString() }))}
                                        isSearchable={false}
                                        menuPlacement="auto"
                                        classNamePrefix="react-select"
                                        formatOptionLabel={(option) => <span className="fw-bold">{option.label}</span>}
                                        components={{ IndicatorSeparator: () => null }}
                                        theme={(theme) => ({
                                            ...theme,
                                            borderRadius: 8,
                                            colors: {
                                                ...theme.colors,
                                                primary: '#2f5aa2',
                                                neutral20: '#adaeac',
                                                neutral80: '#16191f',
                                            },
                                        })}
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                minHeight: '36px',
                                                minWidth: '90px',
                                                fontSize: '0.875rem',
                                            }),
                                            valueContainer: (base) => ({
                                                ...base,
                                                padding: '0 8px',
                                                justifyContent: 'center',
                                            }),
                                            dropdownIndicator: (base) => ({
                                                ...base,
                                                padding: '4px 8px'
                                            }),
                                            menu: (base) => ({
                                                ...base,
                                                zIndex: 9999
                                            })
                                        }}
                                    />
                                ) : (
                                    <div style={{ width: '80px' }} className="d-flex justify-content-center">
                                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ]),
                        ...(tablePagingConfig.isMergedStyle && ableSearch ? [
                            renderSearchBox(true)
                        ] : [])
                    ],
                    inlineButtons: [
                        ...(tablePagingConfig.inlineButtons || []),
                        ...(tablePagingConfig.isMergedStyle ? [
                            <a
                                key="refresh-link"
                                href="#!"
                                role="button"
                                className="text-primary d-flex align-items-center fw-bold text-decoration-none fs-9"
                                style={{ transition: 'color 0.2s' }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setLocalReloadKey(prev => prev + 1);
                                    if (tablePagingConfig.onRefresh) tablePagingConfig.onRefresh();
                                }}
                            >
                                <FontAwesomeIcon icon={faSync} className="me-2" spin={loading} />Refresh
                            </a>
                        ] : [])
                    ]
                }} />
            <div className={cx("paginationBar", "d-flex flex-wrap align-items-center justify-content-between py-3 px-4 mt-3 mb-1 rounded-3 fs-9")} style={{ backgroundColor: '#fff', border: '1px solid #e2e8f0' }}>
                <div className="col-auto d-flex align-items-center">
                    {tablePagingConfig.isMergedStyle && (
                        <div key="rows-per-page-footer" className="d-flex align-items-center mb-0 me-4">
                            <label className={cx("rowsLabel", "form-label me-2 mb-0 fw-semibold text-body-emphasis fs-9")} htmlFor="validationCustom05">Rows per page:</label>
                            {settingsLoaded ? (
                                <Select
                                    value={{ value: pageSize, label: pageSize.toString() }}
                                    onChange={(selectedOption) => {
                                        const val = selectedOption.value;
                                        onchangePageSize(val);
                                        setPageNumber(1);
                                    }}
                                    options={[10, 20, 50, 100, 200].map(n => ({ value: n, label: n.toString() }))}
                                    isSearchable={false}
                                    menuPlacement="auto"
                                    classNamePrefix="react-select"
                                    formatOptionLabel={(option) => <span className="fw-bold">{option.label}</span>}
                                    components={{ IndicatorSeparator: () => null }}
                                    theme={(theme) => ({
                                        ...theme,
                                        borderRadius: 8,
                                        colors: {
                                            ...theme.colors,
                                            primary: '#2f5aa2',
                                            neutral20: '#adaeac',
                                            neutral80: '#16191f',
                                        },
                                    })}
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '36px',
                                            minWidth: '90px',
                                            fontSize: '0.875rem',
                                        }),
                                        valueContainer: (base) => ({
                                            ...base,
                                            padding: '0 8px',
                                            justifyContent: 'center',
                                        }),
                                        dropdownIndicator: (base) => ({
                                            ...base,
                                            padding: '4px 8px'
                                        }),
                                        menu: (base) => ({
                                            ...base,
                                            zIndex: 9999
                                        })
                                    }}
                                />
                            ) : (
                                <div style={{ width: '80px' }} className="d-flex justify-content-center">
                                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    <p className={cx("itemInfo", "mb-0 d-none d-sm-block me-3 fw-semibold text-body")} data-list-info="data-list-info">
                        {
                            tableState.totalRecord === 0 ? "No items" : <Fragment> {(pageNumber - 1) === 0 ? 1 : (pageNumber - 1)*pageSize} to {pageNumber*pageSize} <span className="text-body-tertiary"> Items of </span> {tableState.totalRecord} </Fragment>
                        }
                    </p>
                </div>
                <div className="col-auto d-flex">
                    <button className={`page-link ${pageNumber === 1 ? 'disabled' : ''}`} data-list-pagination="prev" onClick={handlePrev} disabled={pageNumber === 1} >
                        <FontAwesomeIcon icon={faChevronLeft}/>
                    </button>
                    <ul className="mb-0 pagination">
                        {
                            handleVisiblePages().map((page, index) => (
                                <li key={index} className={`${page === pageNumber ? "active" : ""} ${page === "..." ? "disabled" : ""}`}>
                                    <button onClick={() => handlePageClick(page)} className="page" type="button">{page}</button>
                                </li>
                            ))
                        }
                    </ul>
                    <button className={`page-link pe-0 ${pageNumber === tableState.totalRecord ? "disabled" : ""}`} data-list-pagination="next" onClick={handleNext} disabled={pageNumber === tableState.totalRecord} >
                        <FontAwesomeIcon icon={faChevronRight}/>
                    </button>
                </div>
            </div>
        </div>
    )
}

export default TablePaging;